/**
 * Image URL utility — handles three CDN origins during the Cloudinary → Unicorn migration:
 *
 * 1. Unicorn CDN (cdn2.unicornsys.xyz) — new primary CDN, uses query-param transforms
 * 2. Cloudinary (res.cloudinary.com) — legacy, still serves existing assets until migrated
 * 3. Legacy proxy (cdn.nuovosito.link) — old Supabase-backed proxy, kept for back-compat
 *
 * Pages render identically regardless of which CDN currently holds the asset.
 */

import { isUnicornCdnUrl, getUnicornOptimizedUrl } from './cdnUpload';

export const PLACEHOLDER_IMAGE = '/placeholder-property.svg';

const DEFAULT_WIDTH = 1300;
const DEFAULT_QUALITY = 80;
const LEGACY_PROXY_BASE_URL = 'https://cdn.nuovosito.link';

const urlCache: Record<string, string> = {};

const isCloudinaryUrl = (url: string): boolean =>
  url.includes('res.cloudinary.com');

/**
 * Build a Cloudinary URL with automatic format, quality and width transformations.
 * Idempotent: if transforms are already present, does not stack them.
 */
const getCloudinaryResizedUrl = (url: string, width: number): string => {
  try {
    // Skip if transforms are already present (idempotency)
    if (/\/upload\/[^/]*[fqw]_/.test(url)) return url;
    const transforms = `f_auto,q_auto,w_${width}`;
    return url.replace('/upload/', `/upload/${transforms}/`);
  } catch {
    return url;
  }
};

/**
 * Returns a resized/optimized URL for any image.
 * Dispatches to the correct transformation builder based on the URL's origin.
 */
export const getResizedImageUrl = (
  imageUrl: string,
  width: number = DEFAULT_WIDTH,
  quality: number = DEFAULT_QUALITY
): string => {
  if (!imageUrl) return imageUrl;

  const cacheKey = `${imageUrl}_${width}_${quality}`;
  if (urlCache[cacheKey]) return urlCache[cacheKey];

  let resultUrl: string;

  if (isUnicornCdnUrl(imageUrl)) {
    resultUrl = getUnicornOptimizedUrl(imageUrl, {
      w: width,
      format: 'webp',
      q: quality,
    });
  } else if (isCloudinaryUrl(imageUrl)) {
    resultUrl = getCloudinaryResizedUrl(imageUrl, width);
  } else {
    // Legacy proxy for Supabase Storage and other sources
    const timestamp = Date.now();
    resultUrl = `${LEGACY_PROXY_BASE_URL}/?url=${encodeURIComponent(imageUrl)}&width=${width}&quality=${quality}&t=${timestamp}`;
  }

  urlCache[cacheKey] = resultUrl;
  return resultUrl;
};

/**
 * URL della foto con il logo dell'agenzia sovrapposto in basso a destra.
 *
 * Il watermark viene applicato al momento della consegna dalla funzione
 * /api/foto: il file originale sul CDN non viene mai modificato e resta
 * disponibile come archivio.
 *
 * Da usare per tutte le foto immobile mostrate al pubblico. L'area admin
 * continua a usare le URL dirette, così chi carica vede la foto reale.
 */
export const getWatermarkedImageUrl = (
  imageUrl: string,
  width: number = DEFAULT_WIDTH
): string => {
  if (!imageUrl) return imageUrl;

  // I segnaposto locali non passano dal CDN e non vanno elaborati.
  if (imageUrl.startsWith('/')) return imageUrl;

  const cacheKey = `wm_${imageUrl}_${width}`;
  if (urlCache[cacheKey]) return urlCache[cacheKey];

  const resultUrl = `/api/foto?url=${encodeURIComponent(imageUrl)}&w=${width}`;
  urlCache[cacheKey] = resultUrl;
  return resultUrl;
};

/**
 * Process XML content replacing image URLs with optimized versions.
 */
export const processXmlImages = (xmlContent: string): string => {
  if (!xmlContent) return xmlContent;

  try {
    const timestamp = Date.now();
    let processedXml = xmlContent;

    if (!processedXml.includes('<!-- Generated:')) {
      processedXml = processedXml.replace('<?xml', `<!-- Generated: ${new Date().toISOString()} t=${timestamp} -->\n<?xml`);
    }

    const imgRegex = /<foto(\d+)>([^<]+)<\/foto\1>/g;
    processedXml = processedXml.replace(imgRegex, (_, number, url) => {
      const resizedUrl = getResizedImageUrl(url.trim(), DEFAULT_WIDTH, DEFAULT_QUALITY);
      return `<foto${number}>${resizedUrl}</foto${number}>`;
    });

    return processedXml;
  } catch (err) {
    console.error('Errore nell\'elaborazione delle immagini XML:', err);
    return xmlContent;
  }
};

export const isValidUrl = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};
